// =============================================================================
// FILE MANAGER COMPONENT
// Browser e editor file per dispositivi remoti
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Folder,
  File,
  FileText,
  FileCode,
  FileJson,
  ChevronRight,
  ChevronUp,
  RefreshCw,
  Download,
  Trash2,
  Edit3,
  Save,
  X,
  Plus,
  FolderPlus,
  AlertCircle,
  Home,
  Upload,
  FolderUp,
} from 'lucide-react'
import api from '../services/api'
import toast from 'react-hot-toast'

interface FileItem {
  name: string
  path: string
  is_dir: boolean
  size: number
  modified: number
  permissions: string
  error?: string
}

interface FileManagerProps {
  deviceId: string
  isOnline: boolean
}

const getFileIcon = (name: string, isDir: boolean) => {
  if (isDir) return <Folder className="w-5 h-5 text-yellow-500" />

  const ext = name.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'py':
      return <FileCode className="w-5 h-5 text-blue-500" />
    case 'json':
      return <FileJson className="w-5 h-5 text-green-500" />
    case 'md':
    case 'txt':
    case 'log':
      return <FileText className="w-5 h-5 text-gray-500" />
    case 'sh':
    case 'bash':
      return <FileCode className="w-5 h-5 text-orange-500" />
    default:
      return <File className="w-5 h-5 text-gray-400" />
  }
}

const formatSize = (bytes: number) => {
  if (bytes === 0) return '-'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const formatDate = (timestamp: number) => {
  if (!timestamp) return '-'
  return new Date(timestamp * 1000).toLocaleString('it-IT')
}

export default function FileManager({ deviceId, isOnline }: FileManagerProps) {
  const [currentPath, setCurrentPath] = useState('/opt/intelliexpo')
  const [items, setItems] = useState<FileItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Editor state
  const [editingFile, setEditingFile] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState('')
  const [originalContent, setOriginalContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [isBinary, setIsBinary] = useState(false)

  // New file/folder dialog
  const [showNewDialog, setShowNewDialog] = useState<'file' | 'folder' | null>(null)
  const [newName, setNewName] = useState('')

  // Upload state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, filename: '' })

  const loadDirectory = useCallback(async (path: string) => {
    if (!isOnline) {
      setError('Dispositivo offline')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await api.post(`/devices/${deviceId}/files`, {
        operation: 'list',
        params: { path }
      })

      if (response.data.success) {
        setItems(response.data.data.items || [])
        setCurrentPath(response.data.data.path)
      } else {
        setError(response.data.error || 'Errore caricamento directory')
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore comunicazione')
    } finally {
      setLoading(false)
    }
  }, [deviceId, isOnline])

  useEffect(() => {
    if (isOnline) {
      loadDirectory(currentPath)
    }
  }, [isOnline])

  const navigateUp = () => {
    const parentPath = currentPath.split('/').slice(0, -1).join('/') || '/'
    loadDirectory(parentPath)
  }

  const openItem = async (item: FileItem) => {
    if (item.is_dir) {
      loadDirectory(item.path)
    } else {
      // Apri file nell'editor
      setLoading(true)
      try {
        const response = await api.post(`/devices/${deviceId}/files`, {
          operation: 'read',
          params: { path: item.path }
        })

        if (response.data.success) {
          const data = response.data.data
          setEditingFile(item.path)
          setFileContent(data.content)
          setOriginalContent(data.content)
          setIsBinary(data.is_binary)
        } else {
          toast.error(response.data.error || 'Errore lettura file')
        }
      } catch (err: any) {
        toast.error(err.response?.data?.detail || 'Errore comunicazione')
      } finally {
        setLoading(false)
      }
    }
  }

  const saveFile = async () => {
    if (!editingFile) return

    setSaving(true)
    try {
      const response = await api.post(`/devices/${deviceId}/files`, {
        operation: 'write',
        params: {
          path: editingFile,
          content: fileContent,
          is_binary: isBinary
        }
      })

      if (response.data.success) {
        toast.success('File salvato')
        setOriginalContent(fileContent)
      } else {
        toast.error(response.data.error || 'Errore salvataggio')
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Errore comunicazione')
    } finally {
      setSaving(false)
    }
  }

  const closeEditor = () => {
    if (fileContent !== originalContent) {
      if (!confirm('Ci sono modifiche non salvate. Chiudere comunque?')) {
        return
      }
    }
    setEditingFile(null)
    setFileContent('')
    setOriginalContent('')
  }

  const deleteItem = async (item: FileItem) => {
    if (!confirm(`Eliminare ${item.is_dir ? 'la cartella' : 'il file'} "${item.name}"?`)) {
      return
    }

    try {
      const response = await api.post(`/devices/${deviceId}/files`, {
        operation: 'delete',
        params: { path: item.path }
      })

      if (response.data.success) {
        toast.success('Eliminato')
        loadDirectory(currentPath)
      } else {
        toast.error(response.data.error || 'Errore eliminazione')
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Errore comunicazione')
    }
  }

  const createNew = async () => {
    if (!newName.trim()) {
      toast.error('Nome richiesto')
      return
    }

    const newPath = `${currentPath}/${newName}`.replace('//', '/')

    try {
      if (showNewDialog === 'folder') {
        const response = await api.post(`/devices/${deviceId}/files`, {
          operation: 'mkdir',
          params: { path: newPath }
        })

        if (response.data.success) {
          toast.success('Cartella creata')
          loadDirectory(currentPath)
        } else {
          toast.error(response.data.error || 'Errore creazione')
        }
      } else {
        // Crea file vuoto
        const response = await api.post(`/devices/${deviceId}/files`, {
          operation: 'write',
          params: { path: newPath, content: '', is_binary: false }
        })

        if (response.data.success) {
          toast.success('File creato')
          loadDirectory(currentPath)
        } else {
          toast.error(response.data.error || 'Errore creazione')
        }
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Errore comunicazione')
    }

    setShowNewDialog(null)
    setNewName('')
  }

  // Converte file in base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const result = reader.result as string
        // Rimuovi il prefisso "data:...;base64,"
        const base64 = result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = (error) => reject(error)
    })
  }

  // Determina se un file è binario basandosi sull'estensione
  const isBinaryFile = (filename: string): boolean => {
    const textExtensions = [
      'txt', 'md', 'py', 'js', 'ts', 'tsx', 'jsx', 'json', 'xml', 'html', 'css',
      'scss', 'less', 'yaml', 'yml', 'ini', 'conf', 'cfg', 'sh', 'bash', 'zsh',
      'env', 'gitignore', 'log', 'csv', 'sql', 'rst', 'toml'
    ]
    const ext = filename.split('.').pop()?.toLowerCase() || ''
    return !textExtensions.includes(ext)
  }

  // Upload singolo file
  const uploadSingleFile = async (file: File, targetPath: string): Promise<boolean> => {
    try {
      const isBinary = isBinaryFile(file.name)
      let content: string

      if (isBinary) {
        content = await fileToBase64(file)
      } else {
        content = await file.text()
      }

      const response = await api.post(`/devices/${deviceId}/files`, {
        operation: 'write',
        params: {
          path: targetPath,
          content,
          is_binary: isBinary
        }
      })

      return response.data.success
    } catch {
      return false
    }
  }

  // Gestisce upload file
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    const total = files.length
    let success = 0
    let failed = 0

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      setUploadProgress({ current: i + 1, total, filename: file.name })

      const targetPath = `${currentPath}/${file.name}`.replace('//', '/')
      const result = await uploadSingleFile(file, targetPath)

      if (result) {
        success++
      } else {
        failed++
      }
    }

    setUploading(false)
    setUploadProgress({ current: 0, total: 0, filename: '' })

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    if (failed === 0) {
      toast.success(`${success} file caricati con successo`)
    } else {
      toast.error(`Caricati ${success} file, ${failed} falliti`)
    }

    loadDirectory(currentPath)
  }

  // Gestisce upload cartella
  const handleFolderUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    const total = files.length
    let success = 0
    let failed = 0

    // Crea le directory necessarie e carica i file
    const createdDirs = new Set<string>()

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      // webkitRelativePath contiene il path relativo inclusa la cartella root
      const relativePath = (file as any).webkitRelativePath || file.name

      setUploadProgress({ current: i + 1, total, filename: relativePath })

      // Costruisci il path di destinazione
      const targetPath = `${currentPath}/${relativePath}`.replace('//', '/')

      // Crea le directory intermedie se necessario
      const dirPath = targetPath.split('/').slice(0, -1).join('/')
      if (dirPath && !createdDirs.has(dirPath)) {
        try {
          await api.post(`/devices/${deviceId}/files`, {
            operation: 'mkdir',
            params: { path: dirPath }
          })
          createdDirs.add(dirPath)
        } catch {
          // Directory potrebbe già esistere, ignora
        }
      }

      // Carica il file
      const result = await uploadSingleFile(file, targetPath)
      if (result) {
        success++
      } else {
        failed++
      }
    }

    setUploading(false)
    setUploadProgress({ current: 0, total: 0, filename: '' })

    if (folderInputRef.current) {
      folderInputRef.current.value = ''
    }

    if (failed === 0) {
      toast.success(`${success} file caricati con successo`)
    } else {
      toast.error(`Caricati ${success} file, ${failed} falliti`)
    }

    loadDirectory(currentPath)
  }

  const downloadFile = async (item: FileItem) => {
    try {
      const response = await api.post(`/devices/${deviceId}/files`, {
        operation: 'read',
        params: { path: item.path }
      })

      if (response.data.success) {
        const data = response.data.data
        let blob: Blob

        if (data.is_binary) {
          // Decodifica base64
          const binaryString = atob(data.content)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          blob = new Blob([bytes])
        } else {
          blob = new Blob([data.content], { type: 'text/plain' })
        }

        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = item.name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        toast.success('Download avviato')
      } else {
        toast.error(response.data.error || 'Errore download')
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Errore comunicazione')
    }
  }

  // Breadcrumb path
  const pathParts = currentPath.split('/').filter(Boolean)

  if (!isOnline) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
        <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
        <p className="text-yellow-700">Dispositivo offline</p>
        <p className="text-sm text-yellow-600">Il file manager richiede che il dispositivo sia connesso</p>
      </div>
    )
  }

  // Editor view
  if (editingFile) {
    const fileName = editingFile.split('/').pop()
    const hasChanges = fileContent !== originalContent

    return (
      <div className="bg-white rounded-lg shadow">
        {/* Editor header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <FileCode className="w-5 h-5 text-blue-500" />
            <span className="font-medium">{fileName}</span>
            {hasChanges && <span className="text-orange-500 text-sm">(modificato)</span>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={saveFile}
              disabled={saving || !hasChanges}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Salvataggio...' : 'Salva'}
            </button>
            <button
              onClick={closeEditor}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
            >
              <X className="w-4 h-4" />
              Chiudi
            </button>
          </div>
        </div>

        {/* Path */}
        <div className="px-4 py-2 text-sm text-gray-500 bg-gray-50 border-b">
          {editingFile}
        </div>

        {/* Editor */}
        {isBinary ? (
          <div className="p-8 text-center text-gray-500">
            <File className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>File binario - non modificabile</p>
          </div>
        ) : (
          <textarea
            value={fileContent}
            onChange={(e) => setFileContent(e.target.value)}
            className="w-full h-[500px] p-4 font-mono text-sm border-0 focus:ring-0 resize-none"
            spellCheck={false}
          />
        )}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <button
            onClick={() => loadDirectory('/opt/intelliexpo')}
            className="p-2 hover:bg-gray-200 rounded"
            title="Home"
          >
            <Home className="w-4 h-4" />
          </button>
          <button
            onClick={navigateUp}
            disabled={currentPath === '/'}
            className="p-2 hover:bg-gray-200 rounded disabled:opacity-50"
            title="Su"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={() => loadDirectory(currentPath)}
            disabled={loading}
            className="p-2 hover:bg-gray-200 rounded"
            title="Aggiorna"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <div className="h-6 w-px bg-gray-300 mx-1" />
          <button
            onClick={() => setShowNewDialog('file')}
            className="p-2 hover:bg-gray-200 rounded"
            title="Nuovo file"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowNewDialog('folder')}
            className="p-2 hover:bg-gray-200 rounded"
            title="Nuova cartella"
          >
            <FolderPlus className="w-4 h-4" />
          </button>
          <div className="h-6 w-px bg-gray-300 mx-1" />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="p-2 hover:bg-gray-200 rounded"
            title="Carica file"
          >
            <Upload className="w-4 h-4" />
          </button>
          <button
            onClick={() => folderInputRef.current?.click()}
            disabled={uploading}
            className="p-2 hover:bg-gray-200 rounded"
            title="Carica cartella"
          >
            <FolderUp className="w-4 h-4" />
          </button>
          {/* Input file nascosti */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileUpload}
          />
          <input
            ref={folderInputRef}
            type="file"
            // @ts-ignore - webkitdirectory è un attributo non standard ma supportato
            webkitdirectory=""
            multiple
            className="hidden"
            onChange={handleFolderUpload}
          />
        </div>
      </div>

      {/* Upload progress */}
      {uploading && (
        <div className="px-4 py-2 bg-blue-50 border-b flex items-center gap-3">
          <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
          <div className="flex-1">
            <div className="text-sm text-blue-700">
              Caricamento {uploadProgress.current}/{uploadProgress.total}: {uploadProgress.filename}
            </div>
            <div className="h-1.5 bg-blue-200 rounded-full mt-1">
              <div
                className="h-full bg-blue-600 rounded-full transition-all"
                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 px-4 py-2 text-sm bg-gray-100 border-b overflow-x-auto">
        <button
          onClick={() => loadDirectory('/')}
          className="hover:text-blue-600"
        >
          /
        </button>
        {pathParts.map((part, idx) => (
          <span key={idx} className="flex items-center">
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <button
              onClick={() => loadDirectory('/' + pathParts.slice(0, idx + 1).join('/'))}
              className="hover:text-blue-600"
            >
              {part}
            </button>
          </span>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-3 bg-red-50 text-red-700 border-b">
          {error}
        </div>
      )}

      {/* File list */}
      <div className="divide-y max-h-[400px] overflow-y-auto">
        {loading && items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Caricamento...
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Cartella vuota
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.path}
              className="flex items-center px-4 py-2 hover:bg-gray-50 group"
            >
              <div
                className="flex items-center gap-3 flex-1 cursor-pointer min-w-0"
                onClick={() => openItem(item)}
              >
                {getFileIcon(item.name, item.is_dir)}
                <span className="truncate">{item.name}</span>
                {item.error && (
                  <span className="text-xs text-red-500">({item.error})</span>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="w-20 text-right">{formatSize(item.size)}</span>
                <span className="w-40 hidden md:block">{formatDate(item.modified)}</span>
                <span className="w-24 hidden lg:block font-mono text-xs">{item.permissions}</span>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!item.is_dir && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); openItem(item); }}
                        className="p-1 hover:bg-gray-200 rounded"
                        title="Modifica"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); downloadFile(item); }}
                        className="p-1 hover:bg-gray-200 rounded"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteItem(item); }}
                    className="p-1 hover:bg-red-100 text-red-600 rounded"
                    title="Elimina"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* New file/folder dialog */}
      {showNewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">
              {showNewDialog === 'folder' ? 'Nuova cartella' : 'Nuovo file'}
            </h3>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={showNewDialog === 'folder' ? 'Nome cartella' : 'Nome file'}
              className="w-full px-3 py-2 border rounded-lg mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && createNew()}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowNewDialog(null); setNewName(''); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Annulla
              </button>
              <button
                onClick={createNew}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Crea
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
